import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  FileSignature, Upload, RefreshCw, ShieldCheck, ShieldAlert, Play,
  CheckCircle2, XCircle, AlertTriangle, FileText, Clock, Info, Download,
  Power, PowerOff, GitBranch, Anchor, Activity, RotateCw,
} from "lucide-react";

const SEV_STYLES = {
  critical: "bg-red-50 text-red-800 border-red-200",
  high:     "bg-orange-50 text-orange-800 border-orange-200",
  medium:   "bg-amber-50 text-amber-800 border-amber-200",
  low:      "bg-slate-50 text-slate-700 border-slate-200",
  info:     "bg-blue-50 text-blue-700 border-blue-200",
};

const TABS = ["fields", "lint", "coverage", "anchors", "history", "test"];

export default function AdminTemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("fields");
  const [resolvers, setResolvers] = useState([]);
  const [lint, setLint] = useState(null);
  const [lintLoading, setLintLoading] = useState(false);

  // Upload state
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, rRes] = await Promise.all([
        api.get("/agreements/templates"),
        api.get("/agreements/canonical-resolvers"),
      ]);
      const list = tRes.data.templates || [];
      setTemplates(list);
      setResolvers(rRes.data.resolvers || []);
      if (list.length && !selected) setSelected(list[0]);
    } catch (err) {
      toast.error("Failed to load templates: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refreshSelected = async () => {
    if (!selected) return;
    const r = await api.get(`/agreements/templates/${selected._id}`);
    setSelected(r.data);
    setTemplates((prev) => prev.map(t => t._id === r.data._id ? r.data : t));
  };

  const runLint = async () => {
    if (!selected) return;
    setLintLoading(true);
    try {
      const r = await api.get(`/agreements/templates/${selected._id}/lint`);
      setLint(r.data);
    } catch (err) {
      toast.error("Lint failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setLintLoading(false);
    }
  };

  useEffect(() => { if (tab === "lint" && selected) runLint(); }, [tab, selected?._id]);

  // Group by code for the sidebar (each code holds its versions)
  const grouped = useMemo(() => {
    const g = {};
    for (const t of templates) {
      (g[t.code] = g[t.code] || []).push(t);
    }
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => (b.version || "").localeCompare(a.version || ""));
    }
    return g;
  }, [templates]);

  return (
    <div className="p-6" data-testid="template-manager">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A2540] flex items-center gap-2">
            <FileSignature className="w-6 h-6" /> Agreement Templates
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Upload, configure, lint, test, and promote DOCX agreement templates
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="tm-refresh"
            className="h-9 px-3 rounded-md border border-[#E5E7EB] text-sm inline-flex items-center gap-2 hover:bg-[#F9FAFB]"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={() => setUploadOpen(true)}
            data-testid="tm-upload-open"
            className="h-9 px-3 rounded-md bg-[#0066FF] text-white text-sm inline-flex items-center gap-2 hover:bg-[#0052CC]"
          >
            <Upload className="w-4 h-4" /> Upload template
          </button>
        </div>
      </header>

      <div className="grid grid-cols-12 gap-6">
        {/* Template list */}
        <aside className="col-span-3 border border-[#E5E7EB] rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-[#E5E7EB] text-xs font-medium text-[#6B7280] uppercase tracking-wide">Templates</div>
          {loading ? (
            <div className="p-4 text-sm text-[#6B7280]">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="p-4 text-sm text-[#6B7280]">No templates yet. Click Upload to register one.</div>
          ) : (
            <ul className="divide-y divide-[#F1F5F9]" data-testid="tm-template-list">
              {Object.entries(grouped).map(([code, versions]) => (
                <li key={code}>
                  <div className="px-3 py-2 bg-[#F9FAFB] text-xs font-semibold text-[#0A2540]">
                    {code.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </div>
                  {versions.map(t => (
                    <button
                      key={t._id}
                      onClick={() => { setSelected(t); setTab("fields"); }}
                      data-testid={`tm-tpl-${t._id}`}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#F9FAFB] ${
                        selected?._id === t._id ? "bg-blue-50" : ""
                      }`}
                    >
                      <div>
                        <div className="font-medium text-[#0A2540]">v{t.version}</div>
                        <div className="text-xs text-[#6B7280]">{Object.keys(t.discovered_fields || {}).length} fields</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        t.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        {t.status}
                      </span>
                    </button>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Detail panel */}
        <section className="col-span-9 border border-[#E5E7EB] rounded-lg bg-white overflow-hidden">
          {!selected ? (
            <div className="p-8 text-sm text-[#6B7280]">Select a template on the left.</div>
          ) : (
            <>
              <div className="px-5 pt-5 pb-3 border-b border-[#E5E7EB]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-[#0A2540]" data-testid="tm-detail-name">
                      {selected.name}
                      <span className="ml-2 text-xs text-[#6B7280] font-normal">v{selected.version}</span>
                      <StatusPill status={selected.status} />
                    </h2>
                    <p className="text-xs text-[#6B7280] mt-0.5">
                      sha256: <code className="text-[10px]">{(selected.checksum || "").slice(0, 16)}…</code>
                      {" · "}Registered {selected.created_at?.slice(0, 10)} by {selected.created_by}
                      {selected.checksum_drift && (
                        <span className="ml-2 inline-flex items-center gap-1 text-amber-700 text-[11px]">
                          <AlertTriangle className="w-3 h-3" /> DOCX drift from registration
                        </span>
                      )}
                    </p>
                  </div>
                  <TemplateActions template={selected} onChanged={refreshSelected} onReload={load} />
                </div>
                <div className="mt-4 flex gap-1 border-b -mb-3">
                  {TABS.map(t => (
                    <button
                      key={t}
                      onClick={() => setTab(t)}
                      data-testid={`tm-tab-${t}`}
                      className={`px-3 h-9 text-sm border-b-2 -mb-px ${
                        tab === t ? "border-[#0066FF] text-[#0A2540] font-medium" : "border-transparent text-[#6B7280] hover:text-[#0A2540]"
                      }`}
                    >
                      {t === "fields" ? "Fields"
                        : t === "lint" ? "Lint"
                        : t === "coverage" ? "Coverage"
                        : t === "anchors" ? "Signing Anchors"
                        : t === "history" ? "Version history"
                        : "Test Generate"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-5">
                {tab === "fields" && <FieldsTab template={selected} resolvers={resolvers} onSaved={refreshSelected} />}
                {tab === "lint" && <LintTab lint={lint} loading={lintLoading} onRerun={runLint} />}
                {tab === "coverage" && <CoverageTab template={selected} />}
                {tab === "anchors" && <AnchorsTab template={selected} />}
                {tab === "history" && <HistoryTab template={selected} allTemplates={templates} />}
                {tab === "test" && <TestGenerateTab template={selected} />}
              </div>
            </>
          )}
        </section>
      </div>

      {uploadOpen && (
        <UploadDialog
          onClose={() => setUploadOpen(false)}
          onCreated={() => { setUploadOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// FIELDS TAB — mapping table with resolver dropdown + required + default + validation
// -----------------------------------------------------------------------------
function FieldsTab({ template, resolvers, onSaved }) {
  const [rows, setRows] = useState(() => buildRows(template));
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");   // all|required|optional|unmapped

  useEffect(() => { setRows(buildRows(template)); }, [template]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "required") return rows.filter(r => r.required);
    if (filter === "optional") return rows.filter(r => !r.required && r.resolver);
    if (filter === "unmapped") return rows.filter(r => !r.resolver);
    return rows;
  }, [rows, filter]);

  const patchRow = (name, patch) => {
    setRows(r => r.map(row => row.name === name ? { ...row, ...patch } : row));
  };

  const save = async () => {
    setSaving(true);
    try {
      const field_mappings = {};
      for (const r of rows) {
        if (!r.resolver) continue;
        const entry = { resolver: r.resolver };
        if (r.required) entry.required = true; else entry.required = false;
        if (r.default) entry.default = r.default;
        if (r.validationType) entry.validation = { type: r.validationType };
        field_mappings[r.name] = entry;
      }
      await api.post(`/agreements/templates/${template._id}/mappings`, { field_mappings });
      toast.success("Field configuration saved");
      onSaved();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail?.error || err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const required = rows.filter(r => r.required).length;
    const unmapped = rows.filter(r => !r.resolver).length;
    return { total, required, optional: total - required - unmapped, unmapped };
  }, [rows]);

  return (
    <div data-testid="tm-fields">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-[#6B7280]">Fields discovered: <strong className="text-[#0A2540]">{stats.total}</strong></span>
          <span className="text-emerald-700">Required: <strong>{stats.required}</strong></span>
          <span className="text-slate-600">Optional: <strong>{stats.optional}</strong></span>
          {stats.unmapped > 0 && <span className="text-red-700">Unmapped: <strong>{stats.unmapped}</strong></span>}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="tm-fields-filter"
            className="h-9 px-2 text-sm border border-[#E5E7EB] rounded-md bg-white"
          >
            <option value="all">All fields</option>
            <option value="required">Required only</option>
            <option value="optional">Optional only</option>
            <option value="unmapped">Unmapped only</option>
          </select>
          <button
            onClick={save}
            disabled={saving}
            data-testid="tm-fields-save"
            className="h-9 px-3 rounded-md bg-[#0066FF] text-white text-sm hover:bg-[#0052CC] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save configuration"}
          </button>
        </div>
      </div>

      <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] text-xs text-[#6B7280] uppercase">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Placeholder</th>
              <th className="text-left px-3 py-2 font-medium">Resolver</th>
              <th className="text-left px-3 py-2 font-medium w-24">Required</th>
              <th className="text-left px-3 py-2 font-medium w-56">Default value</th>
              <th className="text-left px-3 py-2 font-medium w-32">Validation</th>
              <th className="text-left px-3 py-2 font-medium w-16 text-center">Uses</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {filtered.map(row => (
              <tr key={row.name} data-testid={`tm-row-${row.name}`}>
                <td className="px-3 py-2 font-mono text-xs">{`{{${row.name}}}`}</td>
                <td className="px-3 py-2">
                  <select
                    value={row.resolver || ""}
                    onChange={(e) => patchRow(row.name, { resolver: e.target.value })}
                    className="w-full h-8 px-2 text-xs border border-[#E5E7EB] rounded"
                    data-testid={`tm-resolver-${row.name}`}
                  >
                    <option value="">— Not mapped —</option>
                    {resolvers.map(r => (
                      <option key={r.name} value={r.name}>{r.name}{r.required ? " ●" : ""}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={!!row.required}
                    onChange={(e) => patchRow(row.name, { required: e.target.checked })}
                    data-testid={`tm-required-${row.name}`}
                    disabled={!row.resolver}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={row.default || ""}
                    onChange={(e) => patchRow(row.name, { default: e.target.value })}
                    placeholder="(none)"
                    className="w-full h-8 px-2 text-xs border border-[#E5E7EB] rounded"
                    disabled={!row.resolver}
                    data-testid={`tm-default-${row.name}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={row.validationType || ""}
                    onChange={(e) => patchRow(row.name, { validationType: e.target.value })}
                    className="w-full h-8 px-2 text-xs border border-[#E5E7EB] rounded"
                    disabled={!row.resolver}
                    data-testid={`tm-validation-${row.name}`}
                  >
                    <option value="">— none —</option>
                    <option value="email">email</option>
                    <option value="phone">phone</option>
                    <option value="date">date</option>
                    <option value="money">money</option>
                    <option value="zip">zip</option>
                  </select>
                </td>
                <td className="px-3 py-2 text-center text-xs text-[#6B7280]">{row.occurrences}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function buildRows(template) {
  if (!template) return [];
  const discovered = template.discovered_fields || {};
  const cfg = normalizeConfig(template.field_mappings || {});
  return Object.keys(discovered).sort().map(name => {
    const c = cfg[name] || {};
    return {
      name,
      occurrences: discovered[name],
      resolver: c.resolver || "",
      required: c.required === undefined ? null : !!c.required,
      default: c.default ?? "",
      validationType: (c.validation && c.validation.type) || "",
    };
  });
}

function normalizeConfig(field_mappings) {
  const out = {};
  for (const [k, v] of Object.entries(field_mappings || {})) {
    if (typeof v === "string") out[k] = { resolver: v };
    else if (v && typeof v === "object") out[k] = v;
  }
  return out;
}

// -----------------------------------------------------------------------------
// LINT TAB
// -----------------------------------------------------------------------------
function LintTab({ lint, loading, onRerun }) {
  if (loading) return <div className="text-sm text-[#6B7280]">Running linter…</div>;
  if (!lint) return (
    <div>
      <button onClick={onRerun} className="h-9 px-3 rounded-md bg-[#0066FF] text-white text-sm">Run lint</button>
    </div>
  );
  const c = lint.counts_by_severity || {};
  return (
    <div data-testid="tm-lint">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {lint.promotion_ready ? (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-800 text-sm font-medium border border-emerald-200" data-testid="tm-lint-ready">
              <ShieldCheck className="w-4 h-4" /> Promotion-ready
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-50 text-red-800 text-sm font-medium border border-red-200" data-testid="tm-lint-blocked">
              <ShieldAlert className="w-4 h-4" /> Blocked ({lint.blocking_count} critical/high)
            </span>
          )}
          {Object.entries(c).map(([sev, n]) => (
            <span key={sev} className={`text-xs px-2 py-1 rounded-md border ${SEV_STYLES[sev]}`}>
              {sev}: {n}
            </span>
          ))}
        </div>
        <button onClick={onRerun} className="h-9 px-3 rounded-md border border-[#E5E7EB] text-sm inline-flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Re-run
        </button>
      </div>
      <div className="space-y-2">
        {(lint.findings || []).map((f, i) => (
          <div key={i} className={`border rounded-md p-3 ${SEV_STYLES[f.severity]}`}>
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm">
                <span className="uppercase text-[10px] font-bold mr-2">{f.severity}</span>
                {f.rule_id} · {f.title}
              </div>
              <div className="text-xs opacity-70">{f.location}</div>
            </div>
            {f.matched_text && <div className="mt-1 text-xs font-mono">{f.matched_text.slice(0, 200)}</div>}
            {f.remediation && <div className="mt-1 text-xs">💡 {f.remediation}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// COVERAGE TAB — merge-field coverage vs canonical resolvers
// -----------------------------------------------------------------------------
function CoverageTab({ template }) {
  const discovered = template.discovered_fields || {};
  const cfg = normalizeConfig(template.field_mappings || {});
  const total = Object.keys(discovered).length;
  const mapped = Object.values(cfg).filter(c => c.resolver).length;
  const requiredMapped = Object.entries(cfg).filter(([, c]) => c.resolver && c.required).length;
  const unmapped = total - mapped;
  const pct = total ? Math.round((mapped / total) * 100) : 0;

  const requiredResolvers = Object.entries(cfg).filter(([, c]) => c.required).map(([k]) => k);
  const optionalResolvers = Object.entries(cfg).filter(([, c]) => c.resolver && !c.required).map(([k]) => k);

  return (
    <div data-testid="tm-coverage">
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard label="Total fields" value={total} />
        <StatCard label="Mapped" value={mapped} accent="emerald" />
        <StatCard label="Required" value={requiredMapped} accent="blue" />
        <StatCard label="Unmapped" value={unmapped} accent={unmapped ? "red" : "slate"} />
      </div>
      <div className="mb-6">
        <div className="text-xs text-[#6B7280] mb-1">Coverage: {pct}%</div>
        <div className="h-2 bg-[#F1F5F9] rounded overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="text-sm font-medium text-[#0A2540] mb-2">Required fields ({requiredResolvers.length})</div>
          <ul className="border border-[#E5E7EB] rounded-md divide-y divide-[#F1F5F9] max-h-96 overflow-y-auto">
            {requiredResolvers.sort().map(k => (
              <li key={k} className="px-3 py-1.5 text-xs font-mono flex items-center justify-between">
                <span>{`{{${k}}}`}</span>
                <span className="text-[#6B7280]">{discovered[k] || 0}×</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-sm font-medium text-[#0A2540] mb-2">Optional fields ({optionalResolvers.length})</div>
          <ul className="border border-[#E5E7EB] rounded-md divide-y divide-[#F1F5F9] max-h-96 overflow-y-auto">
            {optionalResolvers.sort().map(k => (
              <li key={k} className="px-3 py-1.5 text-xs font-mono flex items-center justify-between">
                <span>{`{{${k}}}`}</span>
                <span className="text-[#6B7280]">{discovered[k] || 0}×</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = "slate" }) {
  const map = {
    emerald: "text-emerald-700",
    blue: "text-blue-700",
    red: "text-red-700",
    slate: "text-[#0A2540]",
  };
  return (
    <div className="border border-[#E5E7EB] rounded-md p-3">
      <div className="text-xs text-[#6B7280]">{label}</div>
      <div className={`text-2xl font-semibold ${map[accent] || map.slate}`}>{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// VERSION HISTORY TAB
// -----------------------------------------------------------------------------
function HistoryTab({ template, allTemplates }) {
  const siblings = (allTemplates || [])
    .filter(t => t.code === template.code)
    .sort((a, b) => (b.version || "").localeCompare(a.version || ""));
  return (
    <div data-testid="tm-history">
      <div className="text-sm text-[#6B7280] mb-3">Every version of <strong className="text-[#0A2540]">{template.code}</strong>. Executed agreements permanently reference their exact template version + checksum.</div>
      <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] text-xs text-[#6B7280] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Version</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Registered</th>
              <th className="text-left px-3 py-2">By</th>
              <th className="text-left px-3 py-2">Fields</th>
              <th className="text-left px-3 py-2">Checksum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {siblings.map(s => (
              <tr key={s._id} className={s._id === template._id ? "bg-blue-50" : ""}>
                <td className="px-3 py-2 font-medium">v{s.version}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    s.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                  }`}>{s.status}</span>
                </td>
                <td className="px-3 py-2 text-xs">{s.created_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2 text-xs">{s.created_by}</td>
                <td className="px-3 py-2 text-xs">{Object.keys(s.discovered_fields || {}).length}</td>
                <td className="px-3 py-2 text-xs font-mono">{(s.checksum || "").slice(0, 12)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// TEST GENERATE TAB — end-to-end sample generation with editable test data
// -----------------------------------------------------------------------------
const DEFAULT_TEST_DATA = {
  loan_id: "L-A46A156745",
  overrides: {
    application: {
      legal_name: "Sample Plaintiff",
      email: "plaintiff@example.com",
      phone: "(216) 555-0100",
      address: { line1: "742 Evergreen Terrace", city: "Cleveland", state: "OH", zip: "44101" },
      accident_date: "2025-11-15",
    },
    attorney: { full_name: "Sample Attorney, Esq." },
    firm: { name: "Sample Law Firm, LLP", address: { line1: "1 Main St", city: "Cleveland", state: "OH", zip: "44102" } },
    payment: { method: "wire" },
  },
};

function TestGenerateTab({ template }) {
  const [dataJSON, setDataJSON] = useState(JSON.stringify(DEFAULT_TEST_DATA, null, 2));
  const [loanIdInput, setLoanIdInput] = useState("");
  const [advancedMode, setAdvancedMode] = useState(false);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);   // full server traceback

  const run = async () => {
    setRunning(true);
    setResult(null);
    setPreviewUrl(null);
    setErrorDetail(null);
    try {
      // Two modes:
      //   * Simple (default): user pastes/types a real loan_id — call
      //     /preview with empty overrides so live data flows through
      //     unchanged. This is what production admins should almost always
      //     use for one-off test renders.
      //   * Advanced: user edits the raw JSON, controlling overrides field-
      //     by-field. Kept for engineers who need to force sample values
      //     without the corresponding loan actually existing.
      let payloadLoanId, payloadOverrides;
      if (advancedMode) {
        const payload = JSON.parse(dataJSON);
        payloadLoanId = payload.loan_id;
        payloadOverrides = payload.overrides || {};
      } else {
        if (!loanIdInput.trim()) {
          toast.error("Enter a loan ID (e.g. L-A46A156745)");
          setRunning(false);
          return;
        }
        payloadLoanId = loanIdInput.trim();
        payloadOverrides = {};
      }
      const r = await api.post("/agreements/preview", {
        loan_id: payloadLoanId,
        template_id: template._id,
        overrides: payloadOverrides,
      });
      setResult(r.data);
      // Fetch the PDF as a blob for inline preview
      try {
        const pdfRes = await api.get(`/agreements/${r.data.agreement._id}/preview.pdf`, { responseType: "blob" });
        setPreviewUrl(URL.createObjectURL(pdfRes.data));
      } catch (e) {
        toast.warning("Preview generated but PDF fetch failed: " + (e.response?.status || e.message));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err instanceof SyntaxError) {
        toast.error("Test data is not valid JSON");
        setErrorDetail({ exception_type: "SyntaxError (client-side)", exception_message: err.message });
      } else if (detail?.missing) {
        toast.error(`Missing required fields: ${detail.missing.join(", ")}`);
        setResult({ agreement: { validation: detail } });
      } else if (detail && typeof detail === "object" && detail.traceback) {
        // Rich diagnostic bundle from the wrapped endpoint
        toast.error(`Test generation failed at ${detail.stage}: ${detail.exception_type}`);
        setErrorDetail(detail);
      } else {
        toast.error("Test generation failed: " + (typeof detail === "string" ? detail : (detail?.error || err.message)));
        setErrorDetail({ exception_type: "Unknown", exception_message: err.message, status: err.response?.status });
      }
    } finally {
      setRunning(false);
    }
  };

  const validation = result?.agreement?.validation;
  return (
    <div data-testid="tm-test-generate">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-[#6B7280]">
          <span className="font-medium text-[#0A2540]">Developer tool.</span> For day-to-day generation
          use the <span className="font-medium">Agreement</span> tab on the loan page — this screen
          bypasses that workflow and is intended for testing template changes.
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#6B7280] inline-flex items-center gap-1.5 cursor-pointer select-none"
                 data-testid="tm-test-advanced-toggle">
            <input
              type="checkbox"
              checked={advancedMode}
              onChange={(e) => setAdvancedMode(e.target.checked)}
              className="rounded border-[#E5E7EB]"
            />
            Advanced (JSON overrides)
          </label>
          <button
            onClick={run}
            disabled={running}
            data-testid="tm-test-run"
            className="h-9 px-3 rounded-md bg-[#0066FF] text-white text-sm inline-flex items-center gap-2 hover:bg-[#0052CC] disabled:opacity-50"
          >
            <Play className="w-4 h-4" /> {running ? "Generating…" : "Test Generate"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          {advancedMode ? (
            <>
              <div className="text-xs font-medium text-[#6B7280] mb-1">Test data (JSON)</div>
              <textarea
                value={dataJSON}
                onChange={(e) => setDataJSON(e.target.value)}
                data-testid="tm-test-data"
                className="w-full h-96 font-mono text-xs p-3 border border-[#E5E7EB] rounded-md bg-[#F9FAFB]"
              />
              <div className="text-[11px] text-amber-700 mt-1.5 leading-relaxed">
                Overrides in this JSON WILL clobber live loan/application data field-by-field.
                Empty <code>overrides: {'{}'}</code> uses live data end-to-end.
              </div>
            </>
          ) : (
            <>
              <div className="text-xs font-medium text-[#6B7280] mb-1">Loan ID</div>
              <input
                type="text"
                value={loanIdInput}
                onChange={(e) => setLoanIdInput(e.target.value)}
                placeholder="L-A46A156745"
                data-testid="tm-test-loan-id"
                className="w-full h-10 font-mono text-sm px-3 border border-[#E5E7EB] rounded-md bg-white"
              />
              <div className="text-[11px] text-[#6B7280] mt-1.5 leading-relaxed">
                The agreement will be rendered from this loan's live application, attorney, and
                funding data — no JSON, no overrides. Fastest way to sanity-check a template
                against a real record.
              </div>
            </>
          )}
        </div>
        <div>
          <div className="text-xs font-medium text-[#6B7280] mb-1">Result</div>
          {!result && !errorDetail ? (
            <div className="border border-dashed border-[#E5E7EB] rounded-md h-96 flex items-center justify-center text-sm text-[#9CA3AF]">
              Click Test Generate to see result
            </div>
          ) : errorDetail ? (
            <div className="border border-red-200 rounded-md h-96 overflow-auto p-3 bg-red-50" data-testid="tm-test-error">
              <div className="text-red-800 text-sm mb-2 inline-flex items-center gap-2 font-semibold">
                <XCircle className="w-4 h-4" /> {errorDetail.exception_type || "Error"}
                {errorDetail.stage && <span className="text-red-600 font-mono text-xs ml-1">@ {errorDetail.stage}</span>}
              </div>
              <div className="text-xs text-red-900 mb-2">{errorDetail.exception_message}</div>
              {errorDetail.template_code && (
                <div className="text-[10px] font-mono text-red-700 mb-1">
                  template: {errorDetail.template_code} v{errorDetail.template_version}
                  {errorDetail.docx_path && (<> · path: {errorDetail.docx_path} · on_disk: {String(errorDetail.docx_exists)}</>)}
                </div>
              )}
              {errorDetail.traceback && (
                <pre className="text-[10px] text-red-900 whitespace-pre-wrap font-mono bg-white/60 border border-red-200 rounded p-2 mt-2 overflow-auto max-h-72" data-testid="tm-test-traceback">
{errorDetail.traceback}
                </pre>
              )}
              {errorDetail.resolved_keys_sample?.length > 0 && (
                <div className="text-[10px] text-red-700 mt-2">
                  Resolved keys sample: {errorDetail.resolved_keys_sample.join(", ")}…
                </div>
              )}
            </div>
          ) : (
            <div className="border border-[#E5E7EB] rounded-md h-96 overflow-auto p-3">
              {validation?.ok ? (
                <div className="text-emerald-700 text-sm mb-3 inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Validation passed
                </div>
              ) : (
                <div className="text-red-700 text-sm mb-3 inline-flex items-center gap-2">
                  <XCircle className="w-4 h-4" /> Validation failed
                </div>
              )}
              {validation?.missing?.length > 0 && (
                <div className="mb-2 text-xs">
                  <div className="font-medium text-red-700">Missing required:</div>
                  <ul className="list-disc pl-5">
                    {validation.missing.map(m => <li key={m}>{m}</li>)}
                  </ul>
                </div>
              )}
              {validation?.invalid && Object.keys(validation.invalid).length > 0 && (
                <div className="mb-2 text-xs">
                  <div className="font-medium text-amber-700">Invalid values:</div>
                  <ul className="list-disc pl-5">
                    {Object.entries(validation.invalid).map(([k, errs]) => (
                      <li key={k}>{k}: {errs.join("; ")}</li>
                    ))}
                  </ul>
                </div>
              )}
              {previewUrl && (
                <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 text-sm underline">
                  <FileText className="w-4 h-4" /> Open sample PDF ({(result?.agreement?.preview_data && Object.keys(result.agreement.preview_data).length) || 0} fields resolved)
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// TEMPLATE ACTIONS — Download, Replace, Activate/Deactivate, Verify Checksum
// -----------------------------------------------------------------------------
function StatusPill({ status }) {
  const cls = status === "active"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`ml-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${cls}`} data-testid="tm-status-pill">
      {status === "active" ? <CheckCircle2 className="w-3 h-3" /> : <PowerOff className="w-3 h-3" />}
      {status}
    </span>
  );
}

function TemplateActions({ template, onChanged, onReload }) {
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadDocx = async () => {
    try {
      const r = await api.get(`/agreements/templates/${template._id}/docx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${template.code}_v${template.version}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed: " + (e.response?.data?.detail || e.message)); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const r = await api.get(`/agreements/templates/${template._id}/checksum-verify`);
      setVerified(r.data);
      if (r.data.matches_docx_b64 && r.data.matches_registration) {
        toast.success(`Checksum verified: ${r.data.computed.slice(0, 16)}…`);
      } else if (r.data.matches_docx_b64) {
        toast.warning("Bytes match docx_b64 but registration checksum drifted");
      } else {
        toast.error("Checksum mismatch — investigate before deploying");
      }
    } catch (e) { toast.error("Verify failed: " + (e.response?.data?.detail || e.message)); }
    finally { setVerifying(false); }
  };

  const setStatus = async (activate) => {
    setBusy(true);
    try {
      const path = activate ? "activate" : "deactivate";
      await api.post(`/agreements/templates/${template._id}/${path}`, {});
      toast.success(activate ? "Activated (other versions of this code deactivated)" : "Deprecated");
      onReload();
    } catch (e) { toast.error("Update failed: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button
        onClick={verify}
        data-testid="tm-verify-checksum"
        className="h-8 px-2.5 rounded-md border border-[#E5E7EB] text-xs inline-flex items-center gap-1.5 hover:bg-[#F9FAFB]"
        title="Recompute sha256 and compare"
      >
        {verifying ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
        Verify
      </button>
      <button
        onClick={downloadDocx}
        data-testid="tm-download-docx"
        className="h-8 px-2.5 rounded-md border border-[#E5E7EB] text-xs inline-flex items-center gap-1.5 hover:bg-[#F9FAFB]"
      >
        <Download className="w-3.5 h-3.5" /> Download DOCX
      </button>
      <button
        onClick={() => setReplaceOpen(true)}
        data-testid="tm-replace-open"
        className="h-8 px-2.5 rounded-md border border-[#E5E7EB] text-xs inline-flex items-center gap-1.5 hover:bg-[#F9FAFB]"
      >
        <RotateCw className="w-3.5 h-3.5" /> Replace
      </button>
      {template.status === "active" ? (
        <button
          onClick={() => setStatus(false)}
          disabled={busy}
          data-testid="tm-deactivate"
          className="h-8 px-2.5 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs inline-flex items-center gap-1.5 hover:bg-amber-100 disabled:opacity-50"
        >
          <PowerOff className="w-3.5 h-3.5" /> Deprecate
        </button>
      ) : (
        <button
          onClick={() => setStatus(true)}
          disabled={busy}
          data-testid="tm-activate"
          className="h-8 px-2.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs inline-flex items-center gap-1.5 hover:bg-emerald-100 disabled:opacity-50"
        >
          <Power className="w-3.5 h-3.5" /> Activate
        </button>
      )}
      {verified && (
        <div className={`ml-2 text-[10px] font-mono px-2 py-0.5 rounded border ${
          verified.matches_docx_b64 ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"
        }`}>
          {verified.matches_docx_b64 ? "✓" : "✗"} {verified.computed.slice(0, 16)}…
        </div>
      )}
      {replaceOpen && (
        <ReplaceTemplateDialog
          template={template}
          onClose={() => setReplaceOpen(false)}
          onDone={() => { setReplaceOpen(false); onReload(); }}
        />
      )}
    </div>
  );
}

function ReplaceTemplateDialog({ template, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [newVersion, setNewVersion] = useState(bumpVersion(template.version));
  const [name, setName] = useState(template.name || "");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!file || !newVersion) { toast.error("File and new version are required"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("new_version", newVersion);
      fd.append("name", name);
      fd.append("description", description);
      await api.post(`/agreements/templates/${template._id}/replace`, fd);
      toast.success(`Replaced with ${template.code} v${newVersion} (activated, previous version deprecated)`);
      onDone();
    } catch (e) { toast.error("Replace failed: " + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-lg" data-testid="tm-replace-dialog">
        <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="text-base font-semibold text-[#0A2540]">Replace {template.code} v{template.version}</h3>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#0A2540]">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-[#6B7280] bg-blue-50 border border-blue-200 rounded p-3">
            The new DOCX becomes a NEW version of <code className="text-[#0A2540]">{template.code}</code>.
            The current v{template.version} is deprecated (kept in version history);
            field mappings + required fields are inherited from the previous version and
            can be adjusted after the upload.
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">New DOCX</label>
            <input type="file" accept=".docx" onChange={e => setFile(e.target.files?.[0])} data-testid="tm-replace-file" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">New version</label>
              <input
                value={newVersion} onChange={e => setNewVersion(e.target.value)}
                className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded"
                data-testid="tm-replace-version"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Display name</label>
              <input
                value={name} onChange={e => setName(e.target.value)}
                className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded"
                data-testid="tm-replace-name"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Notes (optional)</label>
            <textarea
              rows={2} value={description} onChange={e => setDescription(e.target.value)}
              className="w-full text-sm border border-[#E5E7EB] rounded p-2"
              placeholder="What changed in this revision?"
              data-testid="tm-replace-desc"
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#E5E7EB] flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 rounded-md border border-[#E5E7EB] text-sm">Cancel</button>
          <button onClick={submit} disabled={uploading} data-testid="tm-replace-submit" className="h-9 px-3 rounded-md bg-[#0066FF] text-white text-sm disabled:opacity-50">
            {uploading ? "Uploading…" : "Register replacement"}
          </button>
        </div>
      </div>
    </div>
  );
}

function bumpVersion(v) {
  if (!v) return "2.0";
  const parts = String(v).split(".").map(x => parseInt(x, 10) || 0);
  parts[parts.length - 1] = parts[parts.length - 1] + 1;
  return parts.join(".");
}

// -----------------------------------------------------------------------------
// ANCHORS TAB — provider-agnostic signing anchor status + generation stats
// -----------------------------------------------------------------------------
function AnchorsTab({ template }) {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [aRes, sRes] = await Promise.all([
          api.get(`/agreements/templates/${template._id}/anchor-status`),
          api.get(`/agreements/templates/${template._id}/stats`),
        ]);
        if (mounted) { setStatus(aRes.data); setStats(sRes.data); }
      } catch (e) { toast.error("Failed to load: " + (e.response?.data?.detail || e.message)); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [template._id]);
  if (loading) return <div className="text-sm text-[#6B7280]">Scanning DOCX for signing anchors…</div>;
  if (!status) return null;
  const grouped = status.anchors.reduce((acc, a) => { (acc[a.role] = acc[a.role] || []).push(a); return acc; }, {});
  return (
    <div data-testid="tm-anchors">
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard label="Platform anchors" value={status.platform_anchor_count} />
        <StatCard label="Found" value={status.found_count} accent={status.found_count === status.platform_anchor_count ? "emerald" : "amber"} />
        <StatCard label="Missing" value={status.missing_count} accent={status.missing_count === 0 ? "slate" : "red"} />
        <div className="border border-[#E5E7EB] rounded-md p-3">
          <div className="text-xs text-[#6B7280]">Signing status</div>
          <div className={`mt-1 inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
            status.signing_ready ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
          }`}>
            {status.signing_ready ? <><ShieldCheck className="w-3.5 h-3.5" /> Signing-ready</> : <><ShieldAlert className="w-3.5 h-3.5" /> Anchors missing</>}
          </div>
        </div>
      </div>
      {!status.signing_ready && (
        <div className="mb-4 text-xs bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
          <div className="font-medium mb-1 inline-flex items-center gap-1.5"><Anchor className="w-3.5 h-3.5" /> Signing anchors are not yet embedded in this DOCX.</div>
          The signing platform (DocuSign, Dropbox Sign, or any other provider) uses these
          invisible text markers to place signature/initial/date tabs at the correct
          location. Insert each missing anchor below as 1pt white text at the intended
          signing spot.
        </div>
      )}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {["plaintiff", "attorney", "instabridge"].map(role => (
          <div key={role} className="border border-[#E5E7EB] rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-[#F9FAFB] text-xs font-semibold text-[#0A2540] uppercase tracking-wide">
              {role} ({(grouped[role] || []).filter(a => a.present).length}/{(grouped[role] || []).length})
            </div>
            <ul className="divide-y divide-[#F1F5F9]">
              {(grouped[role] || []).map(a => (
                <li key={a.anchor} className="px-3 py-2 text-xs flex items-start gap-2" data-testid={`tm-anchor-${a.role}-${a.kind}${a.ordinal ? "-" + a.ordinal : ""}`}>
                  {a.present
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    : <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] text-[#0A2540] break-all">{a.anchor}</div>
                    <div className="text-[10px] text-[#6B7280]">{a.label}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {stats && (
        <div>
          <div className="text-sm font-medium text-[#0A2540] mb-2 inline-flex items-center gap-1.5"><Activity className="w-4 h-4" /> Generation statistics</div>
          <div className="grid grid-cols-4 gap-4">
            <StatCard label="Total agreements" value={stats.total_agreements} />
            <StatCard label="Finalized" value={stats.by_status?.finalized || 0} accent="emerald" />
            <StatCard label="Drafts" value={stats.by_status?.draft || 0} accent="slate" />
            <StatCard label="Envelopes sent" value={stats.envelopes_sent || 0} accent="blue" />
          </div>
          {stats.last_generated_at && (
            <div className="text-xs text-[#6B7280] mt-2">Last generated {String(stats.last_generated_at).slice(0, 19).replace("T", " ")} UTC</div>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// UPLOAD DIALOG
// -----------------------------------------------------------------------------
function UploadDialog({ onClose, onCreated }) {
  const [file, setFile] = useState(null);
  const [code, setCode] = useState("");
  const [version, setVersion] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [lintPreview, setLintPreview] = useState(null);

  const doLint = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/agreements/templates/lint", fd);
    setLintPreview(r.data);
  };

  const doUpload = async () => {
    if (!file || !code || !version || !name) {
      toast.error("File, code, version, and name are required");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("code", code);
      fd.append("version", version);
      fd.append("name", name);
      fd.append("description", description);
      await api.post("/agreements/templates/register", fd);
      toast.success(`Template ${code} v${version} registered`);
      onCreated();
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="tm-upload-dialog">
        <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#0A2540]">Upload new template</h3>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#0A2540]">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">DOCX file</label>
            <input type="file" accept=".docx" onChange={(e) => { setFile(e.target.files[0]); setLintPreview(null); }} data-testid="tm-upload-file" />
          </div>
          {file && (
            <button onClick={doLint} data-testid="tm-upload-prelint" className="text-sm text-blue-600 underline inline-flex items-center gap-1">
              <ShieldCheck className="w-4 h-4" /> Pre-lint before registering
            </button>
          )}
          {lintPreview && (
            <div className={`text-xs p-3 rounded-md border ${lintPreview.promotion_ready ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
              <div className="font-medium">
                {lintPreview.promotion_ready ? "✓ Promotion-ready" : `⚠ ${lintPreview.blocking_count} critical/high issues`}
              </div>
              <div className="mt-1">Discovered fields: {lintPreview.discovered_field_count} · Findings: {Object.entries(lintPreview.counts_by_severity || {}).map(([s, n]) => `${s}=${n}`).join(", ")}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Code (slug)</label>
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="workers_compensation" data-testid="tm-upload-code" className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Version</label>
              <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0" data-testid="tm-upload-version" className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Display name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Workers' Compensation Funding Agreement" data-testid="tm-upload-name" className="w-full h-9 px-2 text-sm border border-[#E5E7EB] rounded" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="tm-upload-desc" className="w-full text-sm border border-[#E5E7EB] rounded p-2" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#E5E7EB] flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 rounded-md border border-[#E5E7EB] text-sm">Cancel</button>
          <button onClick={doUpload} disabled={uploading} data-testid="tm-upload-submit" className="h-9 px-3 rounded-md bg-[#0066FF] text-white text-sm disabled:opacity-50">
            {uploading ? "Registering…" : "Register template"}
          </button>
        </div>
      </div>
    </div>
  );
}
